/* api.js — fetch wrapper for GET /v1/files via the local proxy (server.js).
   Anthropic blocks browser CORS from localhost, so we never call
   api.anthropic.com from the page. Header building, typed errors,
   429 exponential backoff, and a minimum gap between consecutive calls. */
(function () {
  'use strict';
  const CAD = (window.CAD = window.CAD || {});

  const BASE = '/v1/files';
  const MIN_GAP_MS = 500; // politeness gap between consecutive API calls
  const MAX_RETRIES = 5;

  let lastCallAt = 0;
  let configPromise = null;

  function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  function makeError(type, message, extra) {
    return Object.assign(new Error(message), extra || {}, { type: type });
  }

  function loadConfig() {
    if (!configPromise) {
      configPromise = fetch('/api/config')
        .then(function (res) { return res.ok ? res.json() : { hasEnvKey: false, proxy: false }; })
        .catch(function () { return { hasEnvKey: false, proxy: false }; });
    }
    return configPromise;
  }

  async function hasCredential(apiKeyOverride) {
    const settings = CAD.storage.getSettings();
    const key = String(apiKeyOverride != null ? apiKeyOverride : (settings.apiKey || '')).trim();
    if (key) return true;
    const cfg = await loadConfig();
    return Boolean(cfg.hasEnvKey);
  }

  async function gatedFetch(url, headers) {
    const wait = Math.max(0, lastCallAt + MIN_GAP_MS - Date.now());
    if (wait > 0) await sleep(wait);
    lastCallAt = Date.now();
    return fetch(url, { method: 'GET', headers: headers });
  }

  async function readBody(res) {
    try { return await res.json(); } catch (err) { return null; }
  }

  /**
   * Fetch one page of the file list.
   * @param {object} opts { limit, page } — page is the opaque cursor from a previous nextPage.
   * @param {string} [apiKeyOverride] use this key instead of the stored one (test connection).
   * @returns {Promise<{data: Array, nextPage: string|null}>}
   */
  async function fetchFilesPage(opts, apiKeyOverride) {
    opts = opts || {};
    const settings = CAD.storage.getSettings();
    const apiKey = String(apiKeyOverride != null ? apiKeyOverride : (settings.apiKey || '')).trim();
    if (!(await hasCredential(apiKey))) {
      throw makeError('NO_KEY', 'No API key configured. Add one in Settings or set it in .env.');
    }

    const params = new URLSearchParams();
    params.set('limit', String(Math.min(Math.max(1, opts.limit || 1000), 1000)));
    if (opts.page) params.set('page', opts.page);

    const headers = {
      'anthropic-version': '2023-06-01',
    };
    if (apiKey) headers['x-api-key'] = apiKey;

    const url = BASE + '?' + params.toString();
    let attempt = 0;

    for (;;) {
      let res;
      try {
        res = await gatedFetch(url, headers);
      } catch (err) {
        throw makeError('NETWORK_ERROR', 'Could not reach the local API proxy. Is `npm start` running?', { cause: err });
      }

      if (res.status === 429) {
        if (attempt >= MAX_RETRIES) {
          throw makeError('RATE_LIMITED', 'Rate limited (HTTP 429) after ' + MAX_RETRIES + ' retries. Wait a moment, then sync again.');
        }
        await sleep(Math.min(Math.pow(2, attempt) * 500, 30000));
        attempt++;
        continue;
      }

      const body = await readBody(res);

      if (res.status === 401) {
        const msg = (body && body.error && body.error.message) || '';
        if (msg.indexOf('CORS requests are not allowed for this Organization') !== -1) {
          throw makeError('CORS_DISABLED',
            'Direct browser access is disabled for this key\u2019s organization. Anthropic replied: \u201C' + msg + '\u201D');
        }
        throw makeError('INVALID_KEY', 'Invalid API key (HTTP 401). Check the key in Settings or .env.');
      }

      if (!res.ok) {
        const msg = (body && body.error && body.error.message) || ('HTTP ' + res.status);
        throw makeError('HTTP_ERROR', 'Anthropic API error (HTTP ' + res.status + '): ' + msg, { status: res.status });
      }

      // Defensive parse: never let a surprising envelope crash the sync.
      let data = [];
      if (body && Array.isArray(body.data)) data = body.data;
      else if (body && typeof body.data === 'object' && body.data) data = [body.data];
      else if (body && !('data' in body)) console.warn('[api] Response has no "data" field \u2014 treating as an empty page.', body);

      return { data: data, nextPage: (body && body.next_page) || null };
    }
  }

  CAD.api = {
    fetchFilesPage: fetchFilesPage,
    loadConfig: loadConfig,
    hasCredential: hasCredential,
    async testConnection(apiKey) {
      await fetchFilesPage({ limit: 1 }, apiKey);
      return true;
    },
    describeError(err) {
      if (!err) return 'Unknown error.';
      return err.message || String(err);
    },
  };
})();
